import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Image, Input, Modal, Progress, Space, Tag, Typography, message } from 'antd';
import { CopyOutlined, EditOutlined, ExportOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import Cart from '../Header/Cart';
import { copyToClipboard } from '../../helpers/clipboard';
import { useTranslation } from '../../hooks/useTranslation';
import {
  AiImageEditApiError,
  AiImageEditCapability,
  AiImageEditResult,
  getAiImageEditCapability,
  getAiImageEditTaskStatus,
  submitAiImageEditTask,
} from '../../services/aiImageEdit';
import './index.css';

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90;

interface AiImageEditModalProps {
  open: boolean;
  uploadRecordId: number;
  sourceUrl: string;
  sourceFileName: string;
  isVip: boolean;
  capability?: AiImageEditCapability;
  onCancel: () => void;
  onSuccess: () => void;
}

const AiImageEditModal: React.FC<AiImageEditModalProps> = ({
  open,
  uploadRecordId,
  sourceUrl,
  sourceFileName,
  isVip,
  capability,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [transparentBackground, setTransparentBackground] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AiImageEditResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [pollAttempt, setPollAttempt] = useState(0);
  const [displayedCapability, setDisplayedCapability] = useState(capability);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollGenerationRef = useRef(0);
  const capabilityGenerationRef = useRef(0);

  const clearPolling = useCallback(() => {
    pollGenerationRef.current += 1;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const refreshCapability = useCallback(async () => {
    const generation = ++capabilityGenerationRef.current;
    try {
      const latestCapability = await getAiImageEditCapability();
      if (generation === capabilityGenerationRef.current) {
        setDisplayedCapability(latestCapability);
      }
    } catch {
      // Keep the last known value when the capability endpoint is temporarily unavailable.
    }
  }, []);

  useEffect(() => {
    setDisplayedCapability(capability);
  }, [capability]);

  useEffect(() => {
    if (open) {
      clearPolling();
      setPrompt('');
      setTransparentBackground(false);
      setSubmitting(false);
      setResult(null);
      setErrorMessage('');
      setPollAttempt(0);
      void refreshCapability();
    }
    return () => {
      capabilityGenerationRef.current += 1;
      clearPolling();
    };
  }, [open, uploadRecordId, clearPolling, refreshCapability]);

  const resolveTaskError = (code?: string) => {
    if (!code) {
      return t('aiImageEdit.errors.taskFailed');
    }
    const key = code.startsWith('aiEdit.') ? code.substring('aiEdit.'.length) : 'taskFailed';
    const translated = t(`aiImageEdit.errors.${key}`);
    return translated === `aiImageEdit.errors.${key}` ? t('aiImageEdit.errors.taskFailed') : translated;
  };

  const pollTask = async (taskId: string, attempt: number, generation: number) => {
    if (generation !== pollGenerationRef.current) {
      return;
    }
    if (attempt >= MAX_POLL_ATTEMPTS) {
      setSubmitting(false);
      setErrorMessage(t('aiImageEdit.errors.pollTimeout'));
      return;
    }

    try {
      const status = await getAiImageEditTaskStatus(taskId);
      if (generation !== pollGenerationRef.current) {
        return;
      }
      setPollAttempt(attempt + 1);
      if (status.status === 'success' && status.result) {
        setResult(status.result);
        setSubmitting(false);
        message.success(t('aiImageEdit.success'));
        onSuccess();
        return;
      }
      if (status.status === 'failed') {
        setSubmitting(false);
        setErrorMessage(resolveTaskError(status.errorCode));
        return;
      }
      pollTimerRef.current = setTimeout(
        () => pollTask(taskId, attempt + 1, generation),
        POLL_INTERVAL_MS,
      );
    } catch (error) {
      if (generation !== pollGenerationRef.current) {
        return;
      }
      setSubmitting(false);
      setErrorMessage(error instanceof Error ? error.message : t('aiImageEdit.errors.taskFailed'));
    }
  };

  const handleSubmit = async () => {
    if (!isVip) {
      setShowCart(true);
      return;
    }
    const normalizedPrompt = prompt.trim();
    if (!normalizedPrompt) {
      message.warning(t('aiImageEdit.promptRequired'));
      return;
    }

    clearPolling();
    setSubmitting(true);
    setResult(null);
    setErrorMessage('');
    setPollAttempt(0);
    const generation = pollGenerationRef.current;
    try {
      const accepted = await submitAiImageEditTask(
        uploadRecordId,
        normalizedPrompt,
        transparentBackground,
      );
      setDisplayedCapability((current) => current ? {
        ...current,
        remaining: Math.max(0, current.remaining - 1),
      } : current);
      void refreshCapability();
      await pollTask(accepted.taskId, 0, generation);
    } catch (error) {
      setSubmitting(false);
      setErrorMessage(error instanceof AiImageEditApiError || error instanceof Error
        ? error.message
        : t('aiImageEdit.errors.taskFailed'));
    }
  };

  const handleClose = () => {
    clearPolling();
    onCancel();
  };

  const footer = result ? [
    <Button key="copy" icon={<CopyOutlined />} onClick={() => copyToClipboard(result.url, t('aiImageEdit.linkCopied'))}>
      {t('aiImageEdit.copyLink')}
    </Button>,
    <Button key="open" icon={<ExportOutlined />} onClick={() => window.open(result.url, '_blank', 'noopener,noreferrer')}>
      {t('aiImageEdit.openResult')}
    </Button>,
    <Button key="close" type="primary" onClick={handleClose}>{t('common.close')}</Button>,
  ] : [
    <Button key="cancel" onClick={handleClose}>{t('common.cancel')}</Button>,
    <Button
      key="submit"
      type="primary"
      icon={isVip ? <EditOutlined /> : <ShoppingCartOutlined />}
      loading={submitting}
      disabled={submitting || (isVip && !prompt.trim())}
      onClick={handleSubmit}
    >
      {isVip ? t('aiImageEdit.start') : t('aiImageEdit.openVip')}
    </Button>,
  ];

  return (
    <>
      <Modal
        title={(
          <Space>
            <EditOutlined />
            <span>{t('aiImageEdit.title')}</span>
            <Tag color="gold">VIP</Tag>
          </Space>
        )}
        open={open}
        onCancel={handleClose}
        footer={footer}
        width={result ? 860 : 720}
        destroyOnClose
      >
        {!isVip ? (
          <Alert
            type="warning"
            showIcon
            message={t('aiImageEdit.vipRequiredTitle')}
            description={t('aiImageEdit.vipRequiredDescription')}
          />
        ) : result ? (
          <div className="ai-image-edit-result">
            <div className="ai-image-edit-result-heading">
              <Title level={5}>{t('aiImageEdit.completeTitle')}</Title>
              <Text type="secondary">{t('aiImageEdit.savedToDirectory', { directory: result.dir || t('common.defaultDirectory') })}</Text>
            </div>
            <div className="ai-image-edit-compare">
              <div className="ai-image-edit-preview-card">
                <span>{t('aiImageEdit.original')}</span>
                <Image src={sourceUrl} alt={sourceFileName} />
              </div>
              <div className="ai-image-edit-preview-card ai-image-edit-preview-card-result">
                <span>{t('aiImageEdit.edited')}</span>
                <Image src={result.url} alt={result.fileName} />
              </div>
            </div>
          </div>
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message={t('aiImageEdit.sizeNotice')}
              description={t('aiImageEdit.resultNotice')}
            />
            {displayedCapability && (
              <div className="ai-image-edit-quota">
                <Text type="secondary">{t('aiImageEdit.dailyQuota')}</Text>
                <Tag color={displayedCapability.remaining > 0 ? 'green' : 'red'}>
                  {t('aiImageEdit.dailyQuotaValue', {
                    remaining: displayedCapability.remaining,
                    limit: displayedCapability.dailyLimit,
                  })}
                </Tag>
              </div>
            )}
            <div className="ai-image-edit-compose">
              <div className="ai-image-edit-source">
                <Image src={sourceUrl} alt={sourceFileName} />
                <Text ellipsis={{ tooltip: sourceFileName }}>{sourceFileName}</Text>
              </div>
              <div className="ai-image-edit-prompt">
                <Text strong>{t('aiImageEdit.promptLabel')}</Text>
                <TextArea
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder={t('aiImageEdit.promptPlaceholder')}
                  maxLength={2000}
                  showCount
                  autoSize={{ minRows: 6, maxRows: 10 }}
                  disabled={submitting}
                />
                <Paragraph type="secondary">{t('aiImageEdit.promptHint')}</Paragraph>
                <div className="ai-image-edit-background-option">
                  <Checkbox
                    checked={transparentBackground}
                    disabled={submitting}
                    onChange={(event) => setTransparentBackground(event.target.checked)}
                  >
                    {t('aiImageEdit.transparentBackground')}
                  </Checkbox>
                  <Text type="secondary">{t('aiImageEdit.transparentBackgroundHint')}</Text>
                </div>
              </div>
            </div>
            {submitting && (
              <div className="ai-image-edit-progress">
                <Progress percent={Math.min(92, 12 + pollAttempt * 2)} status="active" showInfo={false} />
                <Text>{t('aiImageEdit.processing')}</Text>
                <Text type="secondary">{t('aiImageEdit.closeHint')}</Text>
              </div>
            )}
            {errorMessage && (
              <Alert type="error" showIcon message={t('aiImageEdit.failedTitle')} description={errorMessage} />
            )}
          </Space>
        )}
      </Modal>
      <Modal
        title={t('aiImageEdit.vipCenter')}
        open={showCart}
        footer={null}
        width="80%"
        onCancel={() => setShowCart(false)}
      >
        <Cart />
      </Modal>
    </>
  );
};

export default AiImageEditModal;
